import * as exifr from 'exifr';

/**
 * Extract all EXIF data from an image file
 * @param {File} file - The image file
 * @returns {Promise<Object>} - The EXIF data object
 */
export const extractAllExifData = async (file) => {
  try {
    const exifData = await exifr.parse(file, {
      gps: true,
      exif: true,
      ifd0: true,
      ifd1: true,
      iptc: true,
      xmp: true,
      icc: true,
      jfif: true,
      ihdr: true,
      iptc: true,
      tiff: true,
      dns: true,
      interop: true
    });
    
    return exifData || {};
  } catch (error) {
    console.error('Error extracting EXIF data:', error);
    return {};
  }
};

/**
 * Extract GPS coordinates from EXIF data
 * @param {Object} exifData - The EXIF data object
 * @returns {Object|null} - GPS coordinates or null
 */
export const extractGPSFromExif = (exifData) => {
  if (exifData && exifData.latitude && exifData.longitude) {
    return {
      latitude: exifData.latitude,
      longitude: exifData.longitude
    };
  }
  return null;
};

/**
 * Convert EXIF data to a format suitable for re-embedding
 * @param {Object} exifData - The original EXIF data
 * @returns {Object} - Formatted EXIF data for re-embedding
 */
export const formatExifForReembedding = (exifData) => {
  const formatted = {};
  
  // Preserve important metadata
  if (exifData.Make) formatted.Make = exifData.Make;
  if (exifData.Model) formatted.Model = exifData.Model;
  if (exifData.DateTime) formatted.DateTime = exifData.DateTime;
  if (exifData.DateTimeOriginal) formatted.DateTimeOriginal = exifData.DateTimeOriginal;
  if (exifData.DateTimeDigitized) formatted.DateTimeDigitized = exifData.DateTimeDigitized;
  if (exifData.Software) formatted.Software = exifData.Software;
  if (exifData.Artist) formatted.Artist = exifData.Artist;
  if (exifData.Copyright) formatted.Copyright = exifData.Copyright;
  
  // Camera settings
  if (exifData.ExposureTime) formatted.ExposureTime = exifData.ExposureTime;
  if (exifData.FNumber) formatted.FNumber = exifData.FNumber;
  if (exifData.ISO) formatted.ISO = exifData.ISO;
  if (exifData.FocalLength) formatted.FocalLength = exifData.FocalLength;
  if (exifData.Flash) formatted.Flash = exifData.Flash;
  
  // GPS data
  if (exifData.latitude) formatted.GPSLatitude = exifData.latitude;
  if (exifData.longitude) formatted.GPSLongitude = exifData.longitude;
  if (exifData.GPSAltitude) formatted.GPSAltitude = exifData.GPSAltitude;
  if (exifData.GPSTimeStamp) formatted.GPSTimeStamp = exifData.GPSTimeStamp;
  if (exifData.GPSDateStamp) formatted.GPSDateStamp = exifData.GPSDateStamp;
  
  return formatted;
};

/**
 * Create a simple EXIF buffer for basic metadata
 * Note: This is a simplified implementation. For full EXIF support,
 * consider using a dedicated library like piexifjs
 * @param {Object} exifData - The EXIF data to embed
 * @returns {ArrayBuffer} - EXIF buffer
 */
export const createSimpleExifBuffer = (exifData) => {
  // This is a placeholder for EXIF re-embedding
  // In a production environment, you'd want to use a library like piexifjs
  // or implement a more complete EXIF writer
  
  const metadata = {
    preserved: true,
    originalExif: exifData,
    processedAt: new Date().toISOString()
  };
  
  return new TextEncoder().encode(JSON.stringify(metadata));
};

/**
 * Store EXIF data in localStorage as backup
 * @param {string} fileId - Unique identifier for the file
 * @param {Object} exifData - The EXIF data to store
 */
export const storeExifDataBackup = (fileId, exifData) => {
  try {
    const key = `exif_backup_${fileId}`;
    localStorage.setItem(key, JSON.stringify(exifData));
  } catch (error) {
    console.error('Error storing EXIF backup:', error);
  }
};

/**
 * Retrieve EXIF data from localStorage backup
 * @param {string} fileId - Unique identifier for the file
 * @returns {Object|null} - Retrieved EXIF data or null
 */
export const retrieveExifDataBackup = (fileId) => {
  try {
    const key = `exif_backup_${fileId}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error retrieving EXIF backup:', error);
    return null;
  }
};

/**
 * Clean up EXIF data backup from localStorage
 * @param {string} fileId - Unique identifier for the file
 */
export const cleanupExifDataBackup = (fileId) => {
  try {
    const key = `exif_backup_${fileId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error cleaning up EXIF backup:', error);
  }
};
